import dayjs from 'dayjs';
import { z } from 'zod';
import {
  ClassIdSchema,
  DBClassClassIdSchema,
  DBClassSubjectClassIdSchema,
  DBClassSubjectSchema,
} from '../classes/schemas';
import { DEFAULT_PROFILE_ICON } from '../common/constants';
import {
  BirthdaySchema,
  EmailSchema,
  HumanNameSchema,
  PhoneNumberSchema,
  ProfileIconSchema,
  SearchStringSchema,
  SlackChannelIdSchema,
  SlackUserIdSchema,
  StaffUserGroupSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import { DEFAULT_USER_SETTINGS } from '../users/constants';
import { DBUserSharedSettingsSchema, UserIdSchema } from '../users/schemas';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBStaffStaffIdSchema = z
  .lazy(() => UserIdSchema)
  .openapi({
    description: 'Unique UUID for the staff member',
    example: 'd1af5e8c-3f4b-4e2a-9c3a-1b2c3d4e5f6g',
  });
export const DBStaffBirthdaySchema = BirthdaySchema.openapi({
  description: "The staff member's birthday in unix timestamp format (milliseconds)",
  example: 631152000000, // 1990-01-01
});
export const DBStaffClassCountSchema = z.number().int().min(0).openapi({
  description: 'The number of classes the staff member is associated with',
  example: 3,
});
export const DBStaffClassesSchema = z
  .record(
    z.lazy(() => DBClassSubjectClassIdSchema),
    z.object({}).strict()
  )
  .openapi({
    description: 'A record of class IDs the staff member is associated with',
    example: {
      'chem#potassium': {},
      'phys#lithium': {},
    },
  });
export const DBStaffCreatedAtSchema = UnixTimestampSchema;
export const DBStaffDisabledSchema = z.boolean();
export const DBStaffEmailSchema = EmailSchema.endsWith('@cognitotuition.com');
export const DBStaffFirstNameSchema = HumanNameSchema;
export const DBStaffGroupsSchema = z
  .array(StaffUserGroupSchema)
  .min(1, 'Staff must be in at least one group');
export const DBStaffLastNameSchema = HumanNameSchema;
export const DBStaffOrgStaffIdSchema = z
  .string()
  .trim()
  .min(1, 'Org staff ID is required')
  .max(64, 'Org staff ID is too long')
  .openapi({
    description:
      'A unique, editable identifier for the staff member, used to link them to org-specific systems.',
    example: 'john.doe',
  });
export const DBStaffPhoneNumberSchema = PhoneNumberSchema;
export const DBStaffProfileIconSchema = ProfileIconSchema;
export const DBStaffSearchStrSchema = SearchStringSchema;
export const DBStaffSettingsSchema = z.lazy(() => DBUserSharedSettingsSchema);
export const DBStaffSlackChannelsSchema = z.array(SlackChannelIdSchema);
export const DBStaffSlackIdSchema = SlackUserIdSchema;

export const DBStaffSchema = z.object({
  staffId: DBStaffStaffIdSchema,
  birthday: DBStaffBirthdaySchema,
  classCount: DBStaffClassCountSchema,
  // Optional to allow removal of a specific class
  classes: DBStaffClassesSchema.optional(),
  createdAt: DBStaffCreatedAtSchema,
  disabled: DBStaffDisabledSchema,
  email: DBStaffEmailSchema,
  firstName: DBStaffFirstNameSchema,
  groups: DBStaffGroupsSchema,
  lastName: DBStaffLastNameSchema,
  orgStaffId: DBStaffOrgStaffIdSchema,
  phoneNumber: DBStaffPhoneNumberSchema,
  profileIcon: DBStaffProfileIconSchema,
  searchStr: DBStaffSearchStrSchema,
  settings: DBStaffSettingsSchema,
  // Optional to allow removal of a specific slack channel
  slackChannels: DBStaffSlackChannelsSchema.optional(),
  slackId: DBStaffSlackIdSchema.optional(),
});

/**
 * Client Schemas
 */

export const StaffStaffIdSchema = DBStaffStaffIdSchema;
export const StaffBirthdaySchema = DBStaffBirthdaySchema;
export const StaffClassCountSchema = DBStaffClassCountSchema;
export const StaffClassesSchema = z.array(
  z.object({
    classId: z.lazy(() => ClassIdSchema),
    subject: z.lazy(() => DBClassSubjectSchema),
    classCode: z.lazy(() => DBClassClassIdSchema),
  })
);
export const StaffCreatedAtSchema = DBStaffCreatedAtSchema;
export const StaffDisabledSchema = DBStaffDisabledSchema;
export const StaffEmailSchema = DBStaffEmailSchema;
export const StaffFirstNameSchema = DBStaffFirstNameSchema;
export const StaffGroupsSchema = DBStaffGroupsSchema;
export const StaffLastNameSchema = DBStaffLastNameSchema;
export const StaffOrgStaffIdSchema = DBStaffOrgStaffIdSchema;
export const StaffPhoneNumberSchema = DBStaffPhoneNumberSchema;
export const StaffProfileIconSchema = DBStaffProfileIconSchema;
export const StaffSearchStrSchema = DBStaffSearchStrSchema;
export const StaffSettingsSchema = DBStaffSettingsSchema;
export const StaffSlackChannelIdsSchema = DBStaffSlackChannelsSchema;
export const StaffSlackUserIdSchema = DBStaffSlackIdSchema;

export const StaffSchema = z.object({
  id: StaffStaffIdSchema.meta({ title: 'Staff ID' }),
  birthday: StaffBirthdaySchema.meta({ title: 'Birthday' }),
  classCount: StaffClassCountSchema.default(0),
  classes: StaffClassesSchema.default([]),
  createdAt: StaffCreatedAtSchema.default(() => dayjs().valueOf()),
  disabled: StaffDisabledSchema.default(false).meta({ title: 'Disabled' }),
  email: StaffEmailSchema.meta({ title: 'Email' }),
  firstName: StaffFirstNameSchema.meta({ title: 'First name' }),
  groups: StaffGroupsSchema.meta({ title: 'Groups' }),
  lastName: StaffLastNameSchema.meta({ title: 'Last name' }),
  orgStaffId: StaffOrgStaffIdSchema.meta({ title: 'Org staff ID' }),
  phoneNumber: StaffPhoneNumberSchema.meta({ title: 'Phone number' }),
  profileIcon: StaffProfileIconSchema.default(DEFAULT_PROFILE_ICON).meta({
    title: 'Profile icon',
  }),
  searchStr: StaffSearchStrSchema.default(''),
  settings: StaffSettingsSchema.default(DEFAULT_USER_SETTINGS).meta({ title: 'Settings' }),
  slackChannelIds: StaffSlackChannelIdsSchema.default([]),
  slackUserId: StaffSlackUserIdSchema.optional().meta({ title: 'Slack user ID' }),
});
